import { moneyWithCurrencyProvided } from '@/filters';
import {
    selectors,
    classes,
    ACTIVE_CURRENCY,
    SHOW_CURRENCY_NAME,
    REMOVE_TRAILING_ZEROS,
} from './constants.js';

export default {
    updateCurrentPrice() {
        const { container } = this;
        if (!container) {
            return;
        }

        const mobileForm = container?.querySelector(
            selectors?.productFormMobile
        );
        if (!mobileForm) {
            return;
        }

        const currentPrice = mobileForm?.querySelector(`[data-current-price]`);
        if (!currentPrice) {
            return;
        }

        const next_subscription_price = this.member_pricing_available
            ? this.next_member_subscription_price
            : this.next_subscription_price;
        const current_price = this.member_pricing_available
            ? this.current_member_price
            : this.current_price;

        if (this.purchaseType === 'subscription') {
            currentPrice.innerHTML = moneyWithCurrencyProvided(
                next_subscription_price * this.quantity,
                ACTIVE_CURRENCY,
                SHOW_CURRENCY_NAME,
                REMOVE_TRAILING_ZEROS
            );
        } else if (mobileForm?.querySelector(`[data-current-price]`)) {
            currentPrice.innerHTML = moneyWithCurrencyProvided(
                current_price * this.quantity,
                ACTIVE_CURRENCY,
                SHOW_CURRENCY_NAME,
                REMOVE_TRAILING_ZEROS
            );
        }

        const originalPrice = mobileForm?.querySelector(
            `[data-price-original]`
        );
        if (!originalPrice) {
            return;
        }

        originalPrice.innerHTML = moneyWithCurrencyProvided(
            this.current_base_price * this.quantity,
            ACTIVE_CURRENCY,
            SHOW_CURRENCY_NAME,
            REMOVE_TRAILING_ZEROS
        );

        if (this.purchaseType === 'subscription') {
            if (current_price > next_subscription_price) {
                originalPrice.style.display = 'block';
            } else {
                originalPrice.style.display = 'none';
            }

            return;
        }

        if (this.current_base_price > current_price) {
            originalPrice.style.display = 'block';
        } else {
            originalPrice.style.display = 'none';
        }
    },

    updatePrices() {
        const { container } = this;
        if (!container) {
            return;
        }

        this.updateOnetimePrices();
        this.updateSubscriptionPrices();
        this.updateCurrentPrice();
        this.updateBadgeVisibility();
    },

    updateBadgeVisibility() {
        const { container } = this;
        if (!container) {
            return;
        }

        const badges = Array.from(
            container.querySelectorAll('.product-subscription-option__badge')
        );
        if (!badges) {
            return;
        }

        if (!badges?.length) {
            return;
        }

        const minSubscriptionPrice = this.min_subscription_price;
        const onetimePrice = this.current_base_price;
        const memberOnetimePrice = this.current_member_price;

        let badgeHidden = true;

        if (
            !![
                !!this.isActiveSubscriber,
                !!this.membership_already_in_cart,
            ]?.includes(true)
        ) {
            if (memberOnetimePrice > minSubscriptionPrice) {
                badgeHidden = false;
            }
        } else if (onetimePrice > minSubscriptionPrice) {
            badgeHidden = false;
        }

        badges.forEach((badge) => {
            if (badgeHidden) {
                badge.classList.add('hidden');
            } else {
                badge.classList.remove('hidden');
            }
        });
    },

    updateOnetimePrices() {
        const container = this.container;
        if (!container) {
            return;
        }

        const currentVariant = this.currentVariant;
        if (!currentVariant) {
            return;
        }

        const showSaleCompare = this.compare_at_price > this.current_price;

        container
            ?.querySelectorAll(`[data-sale-retail-compare-at-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.compare_at_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );

                if (showSaleCompare) {
                    element.style.removeProperty('display');
                } else {
                    element.style.display = 'none';
                }
            });

        container
            ?.querySelectorAll(`[data-sale-retail-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.current_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });

        // The badge advertises the configured discount when there is one, otherwise
        // the saving against the compare-at anchor. Without this the Liquid-rendered
        // percent went stale on every variant change.
        const salePercent = this.has_product_discount
            ? this.product_discount_percent
            : Math.round(100 - (this.current_price / this.compare_at_price) * 100);

        container
            ?.querySelectorAll(`[data-sale-discount-percent]`)
            ?.forEach((element) => {
                const badge = element.closest('.product-price__badge');
                const show =
                    showSaleCompare &&
                    Number.isFinite(salePercent) &&
                    salePercent > 0;

                if (show) {
                    element.innerHTML = `${salePercent}`;
                }

                if (!badge) {
                    return;
                }

                if (show) {
                    badge.style.removeProperty('display');
                } else {
                    badge.style.display = 'none';
                }
            });

        // Subscription PDPs never render the 'product-price' block, so the one-time row
        // in the purchase options carries the only strikethrough those pages get.
        const showOnetimeCompare =
            Number.isFinite(salePercent) &&
            salePercent > 0 &&
            this.compare_at_price > this.current_price;

        container
            ?.querySelectorAll(`[data-onetime-compare-price]`)
            ?.forEach((element) => {
                if (showOnetimeCompare) {
                    element.innerHTML = moneyWithCurrencyProvided(
                        this.compare_at_price,
                        ACTIVE_CURRENCY,
                        SHOW_CURRENCY_NAME,
                        REMOVE_TRAILING_ZEROS
                    );
                    element.style.removeProperty('display');
                } else {
                    element.style.display = 'none';
                }
            });

        container
            ?.querySelectorAll(`[data-onetime-discount-percent]`)
            ?.forEach((element) => {
                if (showOnetimeCompare) {
                    element.innerHTML = `${salePercent}% OFF`;
                    element.style.removeProperty('display');
                } else {
                    element.style.display = 'none';
                }
            });

        container?.querySelectorAll(`[data-price]`)?.forEach((element) => {
            element.innerHTML = moneyWithCurrencyProvided(
                this.current_price,
                ACTIVE_CURRENCY,
                SHOW_CURRENCY_NAME,
                REMOVE_TRAILING_ZEROS
            );
        });

        container
            ?.querySelectorAll(`[data-retail-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.current_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });

        container
            ?.querySelectorAll(`[data-member-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.current_member_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });

        container
            ?.querySelectorAll(`[data-variant-member-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.current_member_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });

        container
            ?.querySelectorAll(`[data-current-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.current_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });
    },

    updateSubscriptionPrices() {
        const container = this.container;
        if (!container) {
            return;
        }

        const currentSubscriptionVariant = this.currentSubscriptionVariant;
        if (!currentSubscriptionVariant) {
            return;
        }

        container
            ?.querySelectorAll(`[data-subscription-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.current_subscription_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });

        container
            ?.querySelectorAll(`[data-current-subscription-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.current_subscription_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });

        container
            ?.querySelectorAll(`[data-current-member-subscription-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.current_member_subscription_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });

        container
            ?.querySelectorAll(`[data-next-subscription-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.next_subscription_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });

        container
            ?.querySelectorAll(`[data-next-member-subscription-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.next_member_subscription_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });

        container
            ?.querySelectorAll(`[data-min-subscription-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.min_subscription_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });

        container
            ?.querySelectorAll(`[data-min-member-subscription-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.min_member_subscription_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });

        container
            ?.querySelectorAll(`[data-current-price]`)
            ?.forEach((element) => {
                element.innerHTML = moneyWithCurrencyProvided(
                    this.current_subscription_price,
                    ACTIVE_CURRENCY,
                    SHOW_CURRENCY_NAME,
                    REMOVE_TRAILING_ZEROS
                );
            });

        // Keep the discount-gated elements in sync with the selected variant /
        // selling plan. Liquid only renders the "X% OFF" badge and the strikethrough
        // compare price when the raw selling-plan adjustment (current_savings_discount_value)
        // is > 0, so mirror that visibility here instead of leaving them stale.
        const subscriptionDiscount =
            this.current_variant_selected_selling_plan_price_adjustments?.[0]
                ?.value || 0;

        container
            ?.querySelectorAll(`[data-subscription-compare-price]`)
            ?.forEach((element) => {
                if (subscriptionDiscount > 0) {
                    element.innerHTML = moneyWithCurrencyProvided(
                        this.current_base_price,
                        ACTIVE_CURRENCY,
                        SHOW_CURRENCY_NAME,
                        REMOVE_TRAILING_ZEROS
                    );
                    element.style.removeProperty('display');
                } else {
                    element.style.display = 'none';
                }
            });

        container
            ?.querySelectorAll(`[data-subscription-discount-percent]`)
            ?.forEach((element) => {
                if (subscriptionDiscount > 0) {
                    element.innerHTML = `${subscriptionDiscount}% OFF`;
                    element.style.removeProperty('display');
                } else {
                    element.style.display = 'none';
                }
            });
    },

    debounceATC(func, wait) {
        let timeout;
        return function (...args) {
            const context = this;

            if (!timeout) {
                func.apply(context, args);
            }

            clearTimeout(timeout);

            timeout = setTimeout(() => {
                timeout = null;
            }, wait);
        };
    },

    handleButtonClick() {
        this.disabled = true;

        setTimeout(() => {
            this.disabled = false;
        }, 1000);
    },

    updateATCButton() {
        const { container } = this;
        if (!container) {
            return;
        }

        const productForm = this.isMobile
            ? container?.querySelector(selectors?.productFormMobile) ||
              container?.querySelector(selectors?.productForm)
            : container?.querySelector(selectors?.productForm);

        if (!productForm) {
            return;
        }

        this.updateSubmitButton(productForm);
        this.updateBuyNowButton(productForm);
    },

    updateSubmitButton(productForm) {
        const submitButton = productForm?.querySelector(
            selectors?.submitButton
        );
        if (!submitButton) {
            return;
        }

        submitButton?.removeEventListener(
            'click',
            this.handleButtonClick.bind(this)
        );
        submitButton?.addEventListener(
            'click',
            this.debounceATC(this.handleButtonClick.bind(this), 1000).bind(this)
        );

        const submitButtonLabel = productForm?.querySelector(
            selectors?.submitButtonLabel
        );
        if (!submitButtonLabel) {
            return;
        }

        const submitButtonSuccessLabel = productForm?.querySelector(
            selectors?.submitButtonSuccessLabel
        );

        const shouldDisable =
            (this.isMembershipProduct &&
                (this.membership_already_in_cart || this.isActiveSubscriber)) ||
            !this.quantity ||
            !this.selectedVariant ||
            !this.selectedVariant?.available;

        // if (shouldDisable) {
        //     submitButton?.classList?.add(classes?.disabled);
        //     submitButton?.setAttribute('disabled', 'disabled');
        // } else {
        //     submitButton?.classList?.remove(classes?.disabled);
        //     submitButton?.removeAttribute('disabled');
        // }

        // submitButtonLabel.innerHTML = this.button_text;

        if (submitButtonSuccessLabel) {
            submitButtonSuccessLabel.innerHTML = this.button_success_text;
        }
    },

    updateBuyNowButton(productForm) {
        const buyNowButton = productForm?.querySelector(
            selectors?.buyNowButton
        );
        if (!buyNowButton) {
            return;
        }

        if (
            this.isMembershipProduct &&
            (this.membership_already_in_cart || this.isActiveSubscriber)
        ) {
            buyNowButton?.classList?.add(classes?.hide);
            return;
        }

        if (!this.selectedVariant || !this.selectedVariant?.available) {
            buyNowButton?.classList?.remove(classes?.hide);
            buyNowButton?.classList?.add(classes?.disabled);
            buyNowButton?.setAttribute('disabled', 'disabled');
            return;
        }

        buyNowButton?.classList?.remove(classes?.hide, classes?.disabled);
        buyNowButton?.removeAttribute('disabled');

        // Subscription and one-time both behave like a direct "Buy now":
        // only disable when there is no quantity selected.
        if (!this.quantity) {
            buyNowButton?.classList?.add(classes?.disabled);
            buyNowButton?.setAttribute('disabled', 'disabled');
        }

        /* const buyNowButtonLabel = buyNowButton?.querySelector(
            selectors?.buyNowButtonLabel
        );

        if (buyNowButtonLabel) {
            buyNowButtonLabel.innerHTML = this.buy_now_button_text;
        } */
    },

    updateStockState() {
        const container = this.container;
        if (!container) {
            return;
        }

        const stockState = container?.querySelector(selectors?.stockState);
        if (!stockState) {
            return;
        }

        const { selectedVariant } = this;
        if (!selectedVariant) {
            stockState?.classList?.add(classes?.hide);

            return;
        }

        if (!selectedVariant?.available) {
            stockState?.classList?.add(classes?.hide);

            return;
        }

        stockState?.classList?.remove(classes?.hide);
    },

    updateQuantityLimits() {
        const container = this.container;
        if (!container) {
            return;
        }

        const limitsContainer = container?.querySelector(
            selectors?.quantityLimits
        );
        if (!limitsContainer) {
            return;
        }

        const { currentVariant } = this;
        if (!currentVariant) {
            return;
        }

        try {
            const limitsData = JSON.parse(
                limitsContainer.dataset.variantLimits
            );
            if (!limitsData) {
                return;
            }

            const variantId = String(currentVariant.id);
            const productLimits = limitsData.productLimits || {
                min: 0,
                max: 0,
            };
            const variantLimits = limitsData.variantLimits[variantId] || {
                min: 0,
                max: 0,
            };

            // Calculate effective limits (variant limits override product limits)
            let minQuantity = productLimits.min;
            if (
                variantLimits.min !== 0 &&
                variantLimits.min > productLimits.min
            ) {
                minQuantity = variantLimits.min;
            }

            let maxQuantity = productLimits.max;
            if (
                variantLimits.max !== 0 &&
                (productLimits.max === 0 ||
                    variantLimits.max < productLimits.max)
            ) {
                maxQuantity = variantLimits.max;
            }

            // Update message
            const messageEl = limitsContainer.querySelector(
                '.product-form__quantity-limits-message'
            );
            if (!messageEl) {
                return;
            }

            if (minQuantity > 0 && maxQuantity > 0) {
                messageEl.textContent = `Order between ${minQuantity} and ${maxQuantity} items per order.`;
                limitsContainer.style.display = 'block';
            } else if (minQuantity > 0) {
                messageEl.textContent = `Minimum ${minQuantity} items per order.`;
                limitsContainer.style.display = 'block';
            } else if (maxQuantity > 0) {
                messageEl.textContent = `Maximum ${maxQuantity} items per order.`;
                limitsContainer.style.display = 'block';
            } else {
                limitsContainer.style.display = 'none';
            }
        } catch {
            // Silently fail if JSON parsing fails or data is invalid
        }
    },

    updateCouponNote() {
        const container = this.container;
        if (!container) {
            return;
        }

        const couponEl = container.querySelector(selectors.couponNote);
        if (!couponEl) {
            return;
        }

        const { currentVariant, product } = this;
        if (!currentVariant || !product) {
            return;
        }

        const variantId = String(currentVariant.id);
        const variantNote = product?.metafields?.custom?.variant_offer_notes?.[variantId] || '';
        const note = (variantNote || product?.metafields?.custom?.offer_note) || '';

        if (!note || typeof note !== 'string') {
            couponEl.style.display = 'none';
            return;
        }

        const lines = note.split('\n');
        couponEl.innerHTML = '';
        lines.forEach((line, index) => {
            const p = document.createElement('p');
            p.className = `product-form__coupon-line--${index + 1}`;
            p.style.marginBottom = '0';
            p.textContent = line;
            couponEl.appendChild(p);
        });

        couponEl.style.display = '';
    },

    // Swap the gallery DOM and re-init the sliders. Kept off the synchronous
    // interaction frame (callers schedule it via requestAnimationFrame) so the
    // click that changed the variant can paint first.
    applySliderHTML(sliderHTML, html) {
        if (!sliderHTML || typeof html !== 'string') {
            return;
        }

        sliderHTML.innerHTML = html;
        sliderHTML?.classList?.remove('loading');

        // initSlider() tears down any existing sliders before re-creating them,
        // so we don't destroy separately here (avoids double-destroy).
        this.initSlider();
    },

    // The gallery markup is variant-specific (different images per variant), so
    // it is fetched from the server. To keep variant changes responsive we:
    //   1. cache the rendered HTML per variant id (no refetch on repeat picks),
    //   2. abort the previous in-flight fetch + ignore stale results when the
    //      user changes options quickly (debounce-by-latest),
    //   3. never block the interaction: callers do NOT await this, and the DOM
    //      swap is deferred to the next frame so the click paints immediately.
    async updateSlider() {
        const container = this.container;
        if (!container) {
            return;
        }

        const product = this.product;
        if (!product) {
            return;
        }

        const handle = product?.handle;
        if (!handle) {
            return;
        }

        const currentVariant = this.currentVariant;
        if (!currentVariant) {
            return;
        }

        const sliderHTML = container?.querySelector('[data-slider-html]');
        if (!sliderHTML) {
            return;
        }

        const variantId = currentVariant?.id;

        // Mark this as the latest requested render; any older async result that
        // resolves after a newer change bails out instead of clobbering the UI.
        this.sliderRequestId = (this.sliderRequestId || 0) + 1;
        const requestId = this.sliderRequestId;

        this.sliderCache = this.sliderCache || new Map();

        const applyDeferred = (html) => {
            // Defer the DOM swap off the interaction frame so the click paints
            // first, and skip it entirely if a newer variant change superseded us.
            requestAnimationFrame(() => {
                if (requestId !== this.sliderRequestId) {
                    return;
                }

                this.applySliderHTML(sliderHTML, html);
            });
        };

        // 1) Cache hit — no network, apply on the next frame.
        if (variantId && this.sliderCache.has(variantId)) {
            applyDeferred(this.sliderCache.get(variantId));
            return;
        }

        sliderHTML?.classList?.add('loading');

        // 2) Abort the previous in-flight gallery fetch.
        if (this.sliderAbortController) {
            this.sliderAbortController.abort();
        }
        const abortController = ('AbortController' in window) ? new AbortController() : null;
        this.sliderAbortController = abortController;

        const params = new URLSearchParams();

        params.append('view', 'slider');
        if (!!variantId) {
            params.append('variant', variantId);
        }

        try {
            const html = await fetch(
                `/products/${handle}?${params.toString()}`,
                abortController ? { signal: abortController.signal } : undefined
            ).then((response) => {
                return response.text();
            });

            if (variantId) {
                this.sliderCache.set(variantId, html);
            }

            // Leave the 'loading' class on — applySliderHTML removes it AFTER the
            // deferred DOM swap. Removing it here (before the rAF swap) would flash
            // the spinner off one frame before the new gallery appears.
            applyDeferred(html);
        } catch {
            // Fetch failed/aborted: no DOM swap will happen, so clear the spinner
            // here — but only if we're still the latest request (an aborted/stale
            // request must not clear the spinner the newest request just set).
            if (requestId === this.sliderRequestId) {
                sliderHTML?.classList?.remove('loading');
            }
        }
    },

    accordionHTML(title = '', content = '', opened = false) {
        return `
        <div
            class="accordion accordion-product accordion--margin-bottom accordion--info"
            role="region"
            aria-label="${title} Accordion"
            ${opened ? 'open' : ''}
            data-close-accordions="product-accordions-${this.product.id}"
        >
            <div class="ui-uppercase accordion-title" type="button" aria-label="Toggle ${title} Accordion">
                <span>${title}</span>
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="7" viewBox="0 0 12 7" fill="none">
                    <path d="M1 6.5L6 1.5L11 6.5" stroke="black"/>
                </svg>
            </div>

            <div class="accordion-product__description" data-accordion-content>
                <div>${content}</div>
            </div>
        </div>
    `;
    },

    updateAccordions() {
        const accordionsContainer = this.container.querySelector(
            selectors.accordionsContainer
        );
        if (!accordionsContainer) {
            return;
        }

        accordionsContainer.innerHTML = '';

        const accordions = this.product?.metafields?.custom_fields?.accordions;
        const accordions_2 = this.product?.metafields?.custom?.accordions;
        const variant_accordions_2 =
            this.currentVariant?.metafields?.custom?.accordions;

        if (
            !accordions?.length &&
            !accordions_2?.length &&
            !variant_accordions_2?.length
        ) {
            return;
        }

        let html = '';
        let current_index = 1;
        const used_accordions = [];

        if (variant_accordions_2?.length) {
            variant_accordions_2.forEach((accordion) => {
                if (!accordion.title || !accordion.content) {
                    return;
                }

                const opened = current_index === 1;

                html += this.accordionHTML(
                    accordion.title,
                    accordion.content,
                    opened
                );

                current_index++;
                used_accordions.push(accordion.title);
            });
        }

        if (accordions_2?.length) {
            accordions_2.forEach((accordion) => {
                if (
                    !accordion.title ||
                    !accordion.content ||
                    used_accordions.includes(accordion.title)
                ) {
                    return;
                }

                const opened = current_index === 1;

                html += this.accordionHTML(
                    accordion.title,
                    accordion.content,
                    opened
                );

                current_index++;
                used_accordions.push(accordion.title);
            });
        }

        if (accordions?.length) {
            accordions.forEach((accordion) => {
                if (
                    !accordion.title ||
                    !accordion.content ||
                    used_accordions.includes(accordion.title)
                ) {
                    return;
                }

                const opened = current_index === 1;

                html += this.accordionHTML(
                    accordion.title,
                    accordion.content,
                    opened
                );

                current_index++;
                used_accordions.push(accordion.title);
            });
        }

        accordionsContainer.innerHTML = html;
    },

    initAccordions() {
        const { container } = this;
        if (!container) {
            return;
        }

        container.addEventListener('click', (event) => {
            const target = event.target;
            if (!target) {
                return;
            }

            const accordion = target.closest('.accordion-product');
            const accordionContent = target.closest('[data-accordion-content]');

            if (!accordion || accordionContent) {
                return;
            }

            if (accordion?.hasAttribute('open')) {
                accordion?.removeAttribute('open');

                return;
            }

            // Accordions marked independent (e.g. Certifications & Safety) sit
            // outside the one-open-at-a-time group: they neither close the
            // others nor get closed by them.
            if (!accordion?.closest(selectors.independentAccordion)) {
                const accordions = Array.prototype.slice.call(
                    container?.querySelectorAll('.accordion-product') || []
                );

                accordions?.forEach((el) => {
                    if (el?.closest(selectors.independentAccordion)) {
                        return;
                    }

                    el?.removeAttribute('open');
                });
            }

            accordion?.setAttribute('open', 'open');
        });
    },

    updatePricesWrapper() {
        const container = this.container;
        if (!container) {
            return;
        }

        const pricesWrapperElements = Array.from(
            container?.querySelectorAll(selectors?.pricesWrapper)
        );
        if (!pricesWrapperElements) {
            return;
        }

        if (!pricesWrapperElements?.length) {
            return;
        }

        if (this.membership_already_in_cart || this.isActiveSubscriber) {
            container?.classList?.add('product--member');

            pricesWrapperElements?.forEach((pricesWrapper) => {
                pricesWrapper?.classList?.add(
                    'product-subscription-options--member'
                );
            });

            return;
        }

        container?.classList?.remove('product--member');

        pricesWrapperElements?.forEach((pricesWrapper) => {
            pricesWrapper?.classList?.remove(
                'product-subscription-options--member'
            );
        });
    },
};
